import React, { useState, useEffect } from 'react';
import { MOCK_SOCIAL_POSTS, MOCK_SOCIAL_TRENDS, INCOMING_SOCIAL_POSTS } from '../constants';
import { SocialPost } from '../types';
import { getSocialMediaAnalysis, generateCounterNarrative } from '../services/geminiService';
import { Twitter, Facebook, MessageCircle, Share2, ThumbsUp, TrendingUp, AlertTriangle, Send, RefreshCw, Smartphone, Radio, Flame, Activity } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';

const SocialListening: React.FC = () => {
  const [posts, setPosts] = useState<SocialPost[]>(MOCK_SOCIAL_POSTS);
  const [analysis, setAnalysis] = useState<string>("Analyzing real-time feed...");
  const [rumorTopic, setRumorTopic] = useState("");
  const [counterNarrative, setCounterNarrative] = useState("");
  const [generatingResponse, setGeneratingResponse] = useState(false);
  const [isLive, setIsLive] = useState(true);

  // Initial Analysis
  useEffect(() => {
    getSocialMediaAnalysis(MOCK_SOCIAL_POSTS, MOCK_SOCIAL_TRENDS).then(setAnalysis);
  }, []);

  // Simulate Live Feed
  useEffect(() => {
    if (!isLive) return;

    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex < INCOMING_SOCIAL_POSTS.length) {
        const newPost = INCOMING_SOCIAL_POSTS[currentIndex];
        setPosts(prev => [newPost, ...prev]);
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 5000); // Add a new post every 5 seconds

    return () => clearInterval(interval);
  }, [isLive]);

  const handleGenerateResponse = async () => {
    if (!rumorTopic) return;
    setGeneratingResponse(true);
    const result = await generateCounterNarrative(rumorTopic);
    setCounterNarrative(result);
    setGeneratingResponse(false);
  };

  // Sentiment Data for Pie Chart
  const sentimentCounts = {
    Positive: posts.filter(p => p.sentiment === 'Positive').length,
    Neutral: posts.filter(p => p.sentiment === 'Neutral').length,
    Negative: posts.filter(p => p.sentiment === 'Negative').length,
  };
  const sentimentData = [
    { name: 'Positive', value: sentimentCounts.Positive, color: '#138808' }, // BJP Green
    { name: 'Neutral', value: sentimentCounts.Neutral, color: '#000080' },   // Blue
    { name: 'Negative', value: sentimentCounts.Negative, color: '#DC2626' }, // Red
  ];

  // Mock Data for Trend Chart (Simulating 24h history + live)
  const sentimentTrendData = [
    { time: '08:00', Positive: 45, Neutral: 20, Negative: 10 },
    { time: '10:00', Positive: 50, Neutral: 25, Negative: 15 },
    { time: '12:00', Positive: 48, Neutral: 30, Negative: 25 }, // Dip
    { time: '14:00', Positive: 60, Neutral: 20, Negative: 20 }, // Recovery
    { time: '16:00', Positive: 75, Neutral: 15, Negative: 10 }, // Strong finish
    { time: '18:00', Positive: 80, Neutral: 10, Negative: 5 },
  ];

  // Helper to parse bold markdown for the AI response
  const parseBold = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-bold text-slate-900">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const renderFormattedText = (text: string) => {
      if (!text) return null;
      return text.split('\n').map((line, i) => <p key={i} className="mb-2 text-sm text-slate-700 leading-relaxed">{parseBold(line)}</p>);
  };

  const maxTrendVolume = Math.max(...MOCK_SOCIAL_TRENDS.map(t => t.volume));

  return (
    <div className="p-6 bg-slate-50 min-h-screen space-y-6">
      <div className="flex justify-between items-center">
        <div>
           <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
             <Smartphone className="text-bjp-saffron" />
             Digital War Room
           </h2>
           <p className="text-sm text-slate-500">Social Listening & Rapid Response Unit</p>
        </div>
        <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${isLive ? 'bg-red-50 text-red-600 border-red-100' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-red-600 animate-pulse' : 'bg-slate-400'}`}></span>
                {isLive ? 'LIVE' : 'OFFLINE'}
            </span>
            <button 
                onClick={() => setIsLive(!isLive)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50"
            >
            <RefreshCw className={`w-4 h-4 ${isLive ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} /> 
            {isLive ? 'Monitoring...' : 'Refresh Feed'}
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Col: Analytics & Trends */}
        <div className="space-y-6">
           
           {/* Sentiment Pie Chart */}
           <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
             <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-slate-500" /> Sentiment Distribution
             </h3>
             <div className="h-48 w-full relative">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={sentimentData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {sentimentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center flex-col pointer-events-none">
                    <span className="text-2xl font-bold text-slate-800">{Math.round((sentimentCounts.Positive / posts.length) * 100)}%</span>
                    <span className="text-xs text-slate-500">Positive</span>
                </div>
             </div>
             <div className="flex justify-center gap-4 text-xs mt-2">
                {sentimentData.map(d => (
                    <div key={d.name} className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }}></div>
                        <span>{d.name}</span>
                    </div>
                ))}
             </div>
           </div>

           {/* Sentiment Trend Area Chart */}
           <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
             <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-slate-500" /> Sentiment Timeline (24h)
             </h3>
             <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sentimentTrendData}>
                    <defs>
                      <linearGradient id="colorPos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#138808" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#138808" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorNeg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#DC2626" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#DC2626" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="time" tick={{fontSize: 10}} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                    <Area type="monotone" dataKey="Positive" stroke="#138808" fillOpacity={1} fill="url(#colorPos)" />
                    <Area type="monotone" dataKey="Negative" stroke="#DC2626" fillOpacity={1} fill="url(#colorNeg)" />
                  </AreaChart>
                </ResponsiveContainer>
             </div>
           </div>

           {/* Trending Hashtags Widget */}
           <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
             <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-500" /> Trending Topics
                </h3>
                <span className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded-full font-medium">Real-time</span>
             </div>
             <div className="space-y-4">
               {MOCK_SOCIAL_TRENDS.map((trend) => (
                 <div key={trend.id} className="group">
                   <div className="flex items-center justify-between mb-1">
                     <div className="flex items-center gap-2">
                       <span className="text-slate-400 font-mono text-sm font-bold text-bjp-saffron">#</span>
                       <span className="text-sm font-semibold text-slate-700 group-hover:text-bjp-saffron transition-colors cursor-pointer">{trend.hashtag.replace('#', '')}</span>
                     </div>
                     <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-500">{trend.volume.toLocaleString()}</span>
                        {trend.trend === 'up' && <TrendingUp className="w-3 h-3 text-green-500" />}
                        {trend.trend === 'down' && <TrendingUp className="w-3 h-3 text-red-500 rotate-180" />}
                        {trend.trend === 'stable' && <div className="w-3 h-0.5 bg-slate-300"></div>}
                     </div>
                   </div>
                   {/* Volume Bar */}
                   <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${trend.trend === 'up' ? 'bg-green-500' : trend.trend === 'down' ? 'bg-red-400' : 'bg-slate-400'}`} 
                        style={{ width: `${(trend.volume / maxTrendVolume) * 100}%` }}
                      ></div>
                   </div>
                 </div>
               ))}
             </div>
             <button className="w-full mt-4 text-xs text-center text-slate-500 hover:text-bjp-saffron underline">View All Trends</button>
           </div>
        </div>

        {/* Center Col: Feed */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-[calc(100vh-140px)]">
           <div className="p-4 border-b border-slate-100 bg-slate-50 rounded-t-xl flex justify-between items-center">
             <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Radio className="w-4 h-4 text-bjp-saffron" /> Live Feed
             </h3>
             <span className="text-xs text-slate-500">{posts.length} posts retrieved</span>
           </div>
           <div className="overflow-y-auto p-4 space-y-4 flex-1">
              {posts.map((post, index) => (
                 <div 
                    key={post.id} 
                    className={`border border-slate-100 rounded-lg p-4 hover:bg-slate-50 transition-all duration-500 ${index === 0 && isLive ? 'bg-orange-50 border-orange-100 animate-fade-in-up' : ''}`}
                 >
                    <div className="flex justify-between items-start mb-2">
                       <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-full ${post.platform === 'Facebook' ? 'bg-blue-100 text-blue-600' : post.platform === 'Twitter' ? 'bg-sky-100 text-sky-500' : post.platform === 'Instagram' ? 'bg-pink-100 text-pink-600' : 'bg-green-100 text-green-600'}`}>
                              {post.platform === 'Facebook' && <Facebook className="w-3 h-3" />}
                              {post.platform === 'Twitter' && <Twitter className="w-3 h-3" />}
                              {post.platform === 'Instagram' && <Smartphone className="w-3 h-3" />}
                              {post.platform === 'WhatsApp' && <MessageCircle className="w-3 h-3" />}
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-800">{post.author}</p>
                            <p className="text-[10px] text-slate-400">{post.timestamp}</p>
                          </div>
                       </div>
                       <span className={`text-[10px] px-2 py-0.5 rounded-full border ${post.sentiment === 'Positive' ? 'bg-green-50 text-green-600 border-green-100' : post.sentiment === 'Negative' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-slate-50 text-slate-600 border-slate-100'}`}>
                          {post.sentiment}
                       </span>
                    </div>
                    <p className="text-sm text-slate-700 mb-3 leading-snug">{post.content}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                       <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {post.likes}</span>
                       <span className="flex items-center gap-1"><Share2 className="w-3 h-3" /> {post.shares}</span>
                    </div>
                 </div>
              ))}
           </div>
        </div>

        {/* Right Col: AI & Actions */}
        <div className="space-y-6">
           
           {/* Daily Strategy AI */}
           <div className="bg-gradient-to-br from-indigo-50 to-white p-5 rounded-xl shadow-sm border border-indigo-100">
              <h3 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-wide">Daily Strategy (AI)</h3>
              <div className="prose prose-sm prose-indigo text-xs">
                 {renderFormattedText(analysis)}
              </div>
           </div>

           {/* Rumor Control */}
           <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
              <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
                 <AlertTriangle className="w-4 h-4 text-orange-500" /> Rumor Control & Rebuttal
              </h3>
              <p className="text-xs text-slate-500 mb-3">Detected a fake narrative? Generate an instant factual rebuttal.</p>
              
              <div className="space-y-3">
                 <input 
                    type="text" 
                    placeholder="E.g. Opposition claims PMAY funds stopped..." 
                    className="w-full text-sm p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-bjp-saffron/20"
                    value={rumorTopic}
                    onChange={(e) => setRumorTopic(e.target.value)}
                 />
                 <button 
                    onClick={handleGenerateResponse}
                    disabled={generatingResponse || !rumorTopic}
                    className="w-full bg-slate-800 text-white py-2 rounded-lg text-sm font-medium hover:bg-slate-900 disabled:opacity-50 flex items-center justify-center gap-2"
                 >
                    {generatingResponse ? 'Drafting Response...' : 'Generate Counter-Narrative'}
                 </button>
              </div>

              {counterNarrative && (
                <div className="mt-4 p-3 bg-orange-50 border border-orange-100 rounded-lg">
                   <h4 className="text-xs font-bold text-orange-800 mb-2">Draft Response:</h4>
                   <div className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                     {renderFormattedText(counterNarrative)}
                   </div>
                   <div className="mt-3 flex gap-2">
                      <button className="flex-1 bg-green-500 text-white text-xs py-1.5 rounded flex items-center justify-center gap-1 hover:bg-green-600">
                        <MessageCircle className="w-3 h-3" /> Send to WhatsApp
                      </button>
                      <button className="flex-1 bg-sky-500 text-white text-xs py-1.5 rounded flex items-center justify-center gap-1 hover:bg-sky-600">
                        <Twitter className="w-3 h-3" /> Tweet
                      </button>
                   </div>
                </div>
              )}
           </div>

           {/* Broadcast */}
           <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
              <h3 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                 <Send className="w-4 h-4 text-bjp-green" /> Broadcast to Karyakartas
              </h3>
              <div className="grid grid-cols-2 gap-2">
                 <button className="p-2 border border-slate-100 rounded bg-slate-50 text-xs font-medium text-slate-600 hover:bg-white hover:shadow-sm transition-all">
                    📢 Shakti Kendra Heads
                 </button>
                 <button className="p-2 border border-slate-100 rounded bg-slate-50 text-xs font-medium text-slate-600 hover:bg-white hover:shadow-sm transition-all">
                    📄 Booth Presidents
                 </button>
                 <button className="p-2 border border-slate-100 rounded bg-slate-50 text-xs font-medium text-slate-600 hover:bg-white hover:shadow-sm transition-all">
                    🚩 Yuva Morcha
                 </button>
                 <button className="p-2 border border-slate-100 rounded bg-slate-50 text-xs font-medium text-slate-600 hover:bg-white hover:shadow-sm transition-all">
                    👩 Mahila Morcha
                 </button>
              </div>
           </div>

        </div>
      </div>
    </div>
  );
};

export default SocialListening;